<template lang="pug">
#family-permitted-function-edit-tab-component
  .list-container.d-flex.flex-row.align-items-center.x-scroll.mt-4
    // left side list
    .member-list.d-flex.flex-column
      .list-title
        span 非公開メンバー
      .list-body
        .list-body-title.d-flex.flex-row.align-items-center
          span.group-select-title 施設関係者グループ
          select.group-select-box(v-model="unuseFilteredOtherUserGroupId")
            option(v-for="option in otherUserGroupOptions" :value="option.value") {{ option.label }}
        .list-body-searchword.d-flex.flex-row.align-items-center
          b-input-group.search-filter-input-group.mr-1(size="md")
            b-input-group-prepend
              b-input-group-text.search-filter-icon
                .search-icon
            b-form-input.search-filter-input(v-model="unuseOtherUserSearchWord" type="search" placeholder="検索ワードを入力してください")
          HelpIcon.d-flex.justify-content-end.align-items-center(popoverId="otheruser-left-search-filter-help" title="検索項目について")
            template(v-slot:text)
              div 検索項目は以下になります。
              div ・施設関係者ユーザー名
        .list-body-line(v-for="member in _filteredUnuseOtherUserGroupMembers" :key="member.id + '-' + member.memberId").d-flex.flex-row
          b-form-checkbox(v-model="member.isTarget")
            span.member-name {{ member.memberName }}（{{ member.groupTypeName }}・{{ member.name }}）

    // center buttons
    .move-buttons.d-flex.flex-column
      button.btn.move-right-btn(@click="changePermittedFunction(items, false)" type="button") 右に移す→
      button.btn.move-left-btn(@click="changePermittedFunction(items, true)" type="button") ←左に移す

    // right side list
    .member-list.d-flex.flex-column
      .list-title
        span 公開先メンバー
      .list-body
        .list-body-title.d-flex.flex-row.align-items-center
          span.group-select-title 施設関係者グループ
          select.group-select-box(v-model="useFilteredOtherUserGroupId")
            option(v-for="option in otherUserGroupOptions" :value="option.value") {{ option.label }}
        .list-body-searchword.d-flex.flex-row.align-items-center
          b-input-group.search-filter-input-group.mr-1(size="md")
            b-input-group-prepend
              b-input-group-text.search-filter-icon
                .search-icon
            b-form-input.search-filter-input(v-model="useOtherUserSearchWord" type="search" placeholder="検索ワードを入力してください")
          HelpIcon.d-flex.justify-content-end.align-items-center(popoverId="otheruser-right-search-filter-help" title="検索項目について")
            template(v-slot:text)
              div 検索項目は以下になります。
              div ・施設関係者ユーザー名
        .list-body-line(v-for="member in _filteredUseOtherUserGroupMembers" :key="member.id + '-' + member.memberId").d-flex.flex-row
          b-form-checkbox(v-model="member.isTarget")
            span.member-name {{ member.memberName }}（{{ member.groupTypeName }}・{{ member.name }}）

  // error messages
  .register-row(v-show="errorMessage")
    .register-group
      .register-value
        .error-message {{ errorMessage }}

  .box-footer.register-footer
    .buttons.m-0
      button.btn.update-btn(@click="onUpdate(items)" type="button") 更新
  
  RelativeLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Prop, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";

import OtherUserGroupsService from "@/commons/services/OtherUserGroups";
import OtherUserGroupMembersService from "@/commons/services/OtherUserGroupMembers";
import OtherUserGroupModel, { OtherUserPermittedFunctions } from "@/commons/models/OtherUserGroup";
import OtherUserGroupTypesService from "@/commons/services/OtherUserGroupTypes";
import OtherUserGroupTypeModel from "@/commons/models/OtherUserGroupType";
import SystemError from "@/commons/models/SystemError";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import RelativeLoading from "@/pc/basics/RelativeLoading.vue";
import HelpIcon from "@/pc/basics/HelpIcon.vue";

/**
 * ### 空床情報の公開先タブコンポーネント
 */
@Component({
  components: {
    RelativeLoading,
    OkCancelModal,
    OkModal,
    HelpIcon
  }
})
export default class OtherUserGroupMemberVacancyEditTab extends Vue {
  /** 施設関係者グループサービス */
  @Inject("OtherUserGroupsService") otherUserGroupsService!: OtherUserGroupsService;
  /** 施設関係者グループメンバーサービス */
  @Inject("OtherUserGroupMembersService") otherUserGroupMembersService!: OtherUserGroupMembersService;
  /** 施設関係者グループ種類サービス */
  @Inject("OtherUserGroupTypesService") otherUserGroupTypesService!: OtherUserGroupTypesService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";

  /** 編集対象の施設関係者メンバー */
  items: Array<MemberListItem> = [];
  /** フィルタリング用 施設関係者グループリスト */
  otherUserGroupOptions: Array<{ value: number; label: string }> = [];
  /** 使用不可リスト フィルタリング選択 施設関係者グループID */
  unuseFilteredOtherUserGroupId: number = 0;
  /** 使用可リスト フィルタリング選択 施設関係者グループID */
  useFilteredOtherUserGroupId: number = 0;
  /** 使用不可リスト 利用者検索ワード */
  unuseOtherUserSearchWord: string = "";
  /** 使用可リスト 利用者検索ワード */
  useOtherUserSearchWord: string = "";

  /** エラーメッセージ */
  @Prop({ required: true, type: String })
  errorMessage!: string;
  /** 設定項目名 */
  @Prop({ required: true })
  type!: keyof OtherUserPermittedFunctions;

  /**
   * ### グループでフィルタリングしたメンバーを返す(使用不可リスト)
   */
  get _filteredUnuseOtherUserGroupMembers(): Array<MemberListItem> {
    const filterdFamiles: Array<MemberListItem> = this.filterdMembers(this.items, false, this.unuseFilteredOtherUserGroupId, this.unuseOtherUserSearchWord);
    return filterdFamiles;
  }

  /**
   * ### グループでフィルタリングしたメンバーを返す(使用可リスト)
   */
  get _filteredUseOtherUserGroupMembers(): Array<MemberListItem> {
    const filterdFamiles: Array<MemberListItem> = this.filterdMembers(this.items, true, this.useFilteredOtherUserGroupId, this.useOtherUserSearchWord);
    return filterdFamiles;
  }

  /**
   * ### メンバーフィルタリング
   */
  public filterdMembers(members: Array<MemberListItem>, used: boolean, filteredOtherUserGroupId: number, searchWord: string): Array<MemberListItem> {
    let filteredMembers: Array<MemberListItem> = [];
    for (const member of members) {
      const key: keyof OtherUserPermittedFunctions = this.type;

      // メンバーの機能使用可否(Propsで渡された項目名のもの)が引数の使用可否と同じもの
      if (member.updatedPermittedFunctions[key] == used) {
        // 施設関係者グループID選択肢が該当する、または「全て」が選択されている
        if (filteredOtherUserGroupId == 0 || member.id == filteredOtherUserGroupId) {
          // 検索ワードが空または該当するものがある
          if (!searchWord || this.wordFilteringOtherUser(member, searchWord)) {
            filteredMembers.push(member);
          }
        }
      }
    }

    return filteredMembers;
  }

  public wordFilteringOtherUser(member: MemberListItem, searchWord: string): boolean {
    const words: string[] = this.$filterUtil.createSearchWord(searchWord);
    for (const word of words) {
      if (-1 < member.memberName.indexOf(word)) {
        return true;
      }
    }
    return false;
  }

  /**
   * ### 画面情報を再作成する
   */
  public async refresh(): Promise<void> {
    this.createView();

    return;
  }

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    await this.createView();

    return;
  }

  /**
   * ### 画面情報作成
   *
   * - フィルタリングで使用するセレクトボックス用項目を作成する
   * - リスト表示対象(権限編集対象)の施設にひもづく全施設関係者グループメンバーを取得する
   */
  public async createView(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    // 施設関係者グループ種類名のMAPを作成する
    const otherUserGroupTypes: Array<OtherUserGroupTypeModel> = await this.getOtherGroupTypes();
    const otherUserGroupTypesMap: Map<number, string> = this.createOtherUserGroupTypesMap(otherUserGroupTypes);

    // 施設関係者グループIDと施設関係者グループ名の変換用にグループ情報のMAPを作る
    const groups: Array<OtherUserGroupModel> = await this.getOtherUserGroups();
    const otherUserGroupOptions: Array<{ value: number; label: string }> = await this.createFilterOptions(groups, otherUserGroupTypesMap);
    this.otherUserGroupOptions = otherUserGroupOptions;

    // リスト表示するための施設関係者ユーザーを取得する
    const members: Array<OtherUserGroupModel> = await this.getOtherUserGroupMembers();
    const items: Array<MemberListItem> = this.createItems(members);
    this.items = items;

    this.isBusy = false;

    return;
  }

  /**
   * ### 施設関係者グループ種類を取得する
   */
  public async getOtherGroupTypes(): Promise<Array<OtherUserGroupTypeModel>> {
    const otherUserGroupTypes: Array<OtherUserGroupTypeModel> = await this.otherUserGroupTypesService.get(userStore);

    return otherUserGroupTypes;
  }

  /**
   * ### 画面表示用に施設関係者グループ種類のMAPを作成する
   */
  public createOtherUserGroupTypesMap(otherUserGroupTypes: Array<OtherUserGroupTypeModel>): Map<number, string> {
    let map: Map<number, string> = new Map<number, string>();

    for (const type of otherUserGroupTypes) {
      map.set(type.id, type.name);
    }

    return map;
  }

  /**
   * ### 全ての施設関係者グループを取得する
   */
  public async getOtherUserGroups(): Promise<Array<OtherUserGroupModel>> {
    let response: Array<OtherUserGroupModel> = [];

    try {
      response = await this.otherUserGroupsService.gets(userStore);
    } catch (error) {
      const systemerror: SystemError = error as SystemError;
      // 413エラーの場合、全件取得した結果がペイロード制限に引っかかったことになるので、index指定で段階的に取得する
      if (systemerror.statusCode == 413) {
        const count: number = await this.otherUserGroupsService.getCount(userStore);
        const loopEnd: number = Math.ceil(count / 300);

        for (let i = 0; i < loopEnd; i++) {
          const params: any = {
            startIndex: i * 300,
            endIndex: i * 300 + 300
          };
          response = response.concat(await this.otherUserGroupsService.gets(userStore, params));
        }
      }
    }
    return response;
  }

  /**
   * ### 施設関係者グループフィルター用選択項目
   *
   * - 検索用に施設関係者グループマスターのMAPを作る
   * - アカウント情報から許可されている施設関係者グループIDの配列を取得する
   * - 施設関係者グループマスターと照合して選択項目の施設関係者グループを作成する
   */
  public createFilterOptions(otherUserGroups: Array<OtherUserGroupModel>, otherUserGroupTypesMap: Map<number, string>): Array<{ value: number; label: string }> {
    let filterOptions: Array<{ value: number; label: string }> = [];

    filterOptions.push({
      value: 0,
      label: "全て"
    });

    let groupMap: Map<number, OtherUserGroupModel> = new Map<number, OtherUserGroupModel>();
    for (const group of otherUserGroups) {
      groupMap.set(group.id, group);
    }

    const allowedGroups: number[] = userStore.loginUser.permissions.otherUserGroups;
    for (const allowedGroup of allowedGroups) {
      const group: OtherUserGroupModel | undefined = groupMap.get(allowedGroup);
      if (group) {
        const groupTypeName: string | undefined = otherUserGroupTypesMap.get(group.groupTypeId);

        filterOptions.push({
          value: group.id,
          label: `${group.name}（${groupTypeName}）`
        });
      }
    }

    return filterOptions;
  }

  /**
   * ### 施設にひもづく施設関係者グループメンバーを取得する
   */
  public async getOtherUserGroupMembers(): Promise<Array<OtherUserGroupModel>> {
    const response: Array<OtherUserGroupModel> = await this.otherUserGroupMembersService.get(userStore);
    return response;
  }

  /**
   * ### 表示情報を作成する
   *
   * - 変更前と変更後の機能使用可否が分かるように、プロパティを追加したクラスで管理する
   */
  public createItems(members: Array<OtherUserGroupModel>): Array<MemberListItem> {
    let items: Array<MemberListItem> = [];

    for (const member of members) {
      items.push({
        ...member,
        // ただの値格納だと参照渡しになるので、ディープコピーする
        updatedPermittedFunctions: Object.assign({}, member.otherUserPermittedFunctions),
        isTarget: false
      });
    }

    return items;
  }

  /**
   * ### メンバーの機能使用可否を変更する
   *
   * - 使用可であれば使用不可、使用不可であれば使用可に変更する
   * - 画面の選択をはずすために変更した家族の選択中(isTarget)はfalse(未選択)にする
   */
  public changePermittedFunction(items: Array<MemberListItem>, changeTagetUsed: boolean): void {
    for (const item of items) {
      if (item.isTarget == false) {
        continue;
      }

      const key: keyof OtherUserPermittedFunctions = this.type;
      if (item.updatedPermittedFunctions[key] == changeTagetUsed) {
        item.updatedPermittedFunctions[key] = !changeTagetUsed;
        item.isTarget = false;
      }
    }

    return;
  }

  /**
   * ### 更新ボタン押下イベント
   */
  public async onUpdate(items: Array<MemberListItem>): Promise<void> {
    let updateTargetMembers: Array<OtherUserGroupModel> = [];

    for (const item of items) {
      const key: keyof OtherUserPermittedFunctions = this.type;
      if (item.updatedPermittedFunctions[key] != item.otherUserPermittedFunctions[key]) {
        item.otherUserPermittedFunctions = item.updatedPermittedFunctions;
        updateTargetMembers.push(item);
      }
    }
    this.$emit("onUpdate", updateTargetMembers);

    return;
  }
}

interface MemberListItem extends OtherUserGroupModel {
  updatedPermittedFunctions: OtherUserPermittedFunctions;
  isTarget: boolean;
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";
@import "../../../styles/pc/_mixin.scss";

#family-permitted-function-edit-tab-component {
  position: relative;
  width: 100%;

  .x-scroll {
    overflow-x: auto;
    padding: 5px;
  }

  .update-btn,
  .cancel-btn {
    width: 120px;
  }

  .message {
    margin: 5px 5px 12px 5px;
  }

  .move-buttons {
    margin: 24px;

    .move-right-btn,
    .move-left-btn {
      width: 140px;
    }
    .move-right-btn {
      margin-bottom: 12px;
    }
  }

  .list-body-searchword {
    .search-filter-input-group {
      .search-filter-icon {
        background-color: #fff !important;
        .search-icon {
          @include svg-icon("../../../assets/search.svg", $font-base-color, 20px, 20px);
        }
      }
      .search-filter-input {
        border-left: 0;
      }
    }
  }

  .member-list {
    min-width: 300px;
    width: calc(50% - 105px);
    // 縦サイズは計算して求め、出来るだけ縦サイズを大きくする
    height: calc(100vh - 334px);
    margin: 5px;

    // デフォルトフォントサイズを指定する
    font-size: 16px;
    // 外枠はここで描画させる
    border: solid 1px $border-darkgray;

    .list-title {
      padding: 5px;
      min-height: 32px;
      font-weight: bold;
      text-align: center;
      background-color: $bg-header;
      border-bottom: solid 1px $border-lightgray;
      min-height: 35px;
    }
    .list-body {
      height: calc(100vh - 300px);
      overflow-y: scroll;
      overflow-x: auto;

      .list-body-title,
      .list-body-searchword {
        height: 48px;
        background-color: $bg-header;

        .group-select-title {
          margin-right: 16px;
        }

        .group-select-box {
          width: calc(100% - 190px);
          height: 34px;
          padding: 0 12px;
          border: 1px solid $form-border-color;
          border-radius: 0.25rem;
          &:focus {
            // chromeが自動的に付与するスタイルを無効化する
            outline: none;
            box-shadow: 0 0 0 0.2rem $form-boxshadow-select-color;
            border: 1px solid $form-border-select-color !important;
            border-radius: 0.25rem;
          }
        }
      }

      .list-body-title {
        padding: 8px;
      }

      .list-body-searchword,
      .list-body-line {
        padding: 8px;
        border-right: solid 1px $border-lightgray;
        border-bottom: solid 1px $border-lightgray;
      }
      .member-name {
        word-break: break-all;
      }
    }
  }

  .box-footer {
    padding: 10px 0 0 0 !important;
  }
}
</style>
